import { useCallback, useEffect, useRef, useState } from 'react';

import Button from 'components/Button';
import {
	LoadingSkeleton,
	LoadingSkeletonItem,
} from 'components/LoadingSkeleton';
import { mapStockStoreMarker, mapStoreMarker, StoreMap } from 'components/Map';
import Message from 'components/Message';
import Popover from 'components/Popover';
import ScreenReaderAnnouncementText from 'components/ScreenReaderAnnouncementText';
import SearchField from 'components/SearchField';
import StoreInfo from 'components/StoreInfo';
import StoreListItem from 'components/StoreListItem';
import Text from 'components/Text';
import { useRelativeDistance } from 'hooks';
import { useLengthFormatter } from 'hooks/format';
import type { Store as ApiStore, StoreStockCard } from 'models/api';
import type { StoreId } from 'types';
import { getTestDataAttrFrom, is, range } from 'utils/helpers';
import { useI18n } from 'utils/i18n';
import { type Coordinate, round } from 'utils/math';

interface BasicStore {
	id: StoreId;
	name: string;
}

type ExtendedStoreStockCardResponse = StoreStockCard & BasicStore;

interface ExtendedStoreStockCardResponseWithDistance extends ExtendedStoreStockCardResponse {
	distance: number;
}

type Store = ExtendedStoreStockCardResponse | ApiStore;

interface Props {
	allStores: Store[] | undefined;
	baseId: string;
	isLoading: boolean;
	isOpen: boolean;
	nearbyStores?: Store[];
	onBackClick?: () => void;
	onClose: () => void;
	onStoreSelectClick: (store: Store) => void;
	selectedStoreId?: StoreId;
	storeDetails?: ApiStore;
	title: string;
}

function ListSkeleton() {
	return (
		<LoadingSkeleton className="space-y-2">
			{range(15).map((i) => (
				<LoadingSkeletonItem key={i} height="4.8rem" />
			))}
		</LoadingSkeleton>
	);
}
ListSkeleton.displayName = 'StorePopover_ListSkeleton';

function StoreList({
	className,
	heading,
	isLoading,
	onStoreSelectClick,
	selectedStoreId,
	stores,
}: {
	className?: string;
	heading?: string;
	isLoading?: boolean;
	onStoreSelectClick: (store: Store) => void;
	selectedStoreId: StoreId | undefined;
	stores?: (Store | ExtendedStoreStockCardResponseWithDistance)[] | undefined;
}) {
	const { t } = useI18n();
	const formatDistance = useLengthFormatter('kilometer');

	return (
		<div className={className}>
			{isLoading && <ListSkeleton />}
			{!isLoading && (
				<>
					<Text as="h3" className="mb-4" text={heading} />
					<ul className="space-y-2">
						{stores?.map((store) => {
							const distance =
								'distance' in store
									? t('general_distance_away_text', {
											distance: formatDistance(round(store.distance / 1000, 1)),
										})
									: undefined;
							const isSelected = store.id === selectedStoreId;

							if ('stock' in store) {
								return (
									<StoreListItem
										key={store?.id}
										name={store?.name}
										isSelected={isSelected}
										inStock={store.stock.inStock}
										stockStatusText={store.stock.text.generic}
										address={`${store.streetAddress}, ${store.postalCode}, ${store.city}`}
										storeArea={store.storeArea || ''}
										distance={distance}
										onClick={() => onStoreSelectClick(store)}
									/>
								);
							}
							return (
								<StoreListItem
									key={store.id}
									name={store?.name}
									storeArea={store.storeArea || ''}
									address={`${store.streetAddress}, ${store.postalCode}, ${store.city}`}
									isSelected={isSelected}
									openHours={store.todaysOpeningHours?.description || ''}
									isOpen={
										store.todaysOpeningHours?.state?.toUpperCase() === 'OPEN'
									}
									distance={distance}
									onClick={() => onStoreSelectClick(store)}
								/>
							);
						})}
					</ul>
				</>
			)}
		</div>
	);
}
StoreList.displayName = 'StorePopover_StoreList';

function ListView({
	allStores,
	isLoading,
	nearbyStores,
	onStoreSelectClick,
	selectedStoreId,
	storesWithDistance,
	userPosition,
}: {
	allStores: Store[] | undefined;
	isLoading: boolean;
	nearbyStores: Store[] | undefined;
	onStoreSelectClick: (store: Store) => void;
	selectedStoreId: StoreId | undefined;
	storesWithDistance: Store[] | undefined;
	userPosition: Coordinate | undefined;
}) {
	const { t } = useI18n();
	return (
		<>
			{isLoading && <ListSkeleton />}
			{!isLoading && (
				<>
					{userPosition && is.arrayWithLength(storesWithDistance) && (
						<StoreList
							selectedStoreId={selectedStoreId}
							onStoreSelectClick={onStoreSelectClick}
							heading={t('stores_for_position_header')}
							stores={storesWithDistance}
						/>
					)}
					{!userPosition && (
						<>
							{is.arrayWithLength(nearbyStores) && (
								<StoreList
									onStoreSelectClick={onStoreSelectClick}
									selectedStoreId={selectedStoreId}
									className="mb-10"
									heading={t('store_popover_nearby_stores_header')}
									stores={nearbyStores}
								/>
							)}
							<StoreList
								selectedStoreId={selectedStoreId}
								onStoreSelectClick={onStoreSelectClick}
								heading={t('store_popover_all_stores_header')}
								stores={allStores}
							/>
						</>
					)}
				</>
			)}
		</>
	);
}
ListView.displayName = 'StorePopover_ListView';

/** Shows a filterable list of stores with a map view and store info view */
export default function StorePopover({
	allStores,
	baseId,
	isLoading: isLoadingProp,
	isOpen,
	nearbyStores,
	onBackClick,
	onClose: onCloseProp,
	onStoreSelectClick,
	selectedStoreId,
	storeDetails,
	title,
}: Props) {
	const { t, tPlural } = useI18n();

	const id = `store-popover-${baseId}`;
	const titleId = `${id}-title`;

	const [searchValue, setSearchValue] = useState('');
	const [isMapVisible, setIsMapVisible] = useState(false);

	const filterStores = (stores: Store[] | undefined = []) =>
		stores.filter((store) =>
			store.name.toLowerCase().includes(searchValue.toLowerCase()),
		);

	const onClose = useCallback(() => {
		setSearchValue('');
		onCloseProp();
	}, [onCloseProp]);

	useEffect(() => {
		if (storeDetails) {
			setSearchValue('');
		}
	}, [storeDetails]);

	const {
		clearUserPosition,
		error: userPositionError,
		getUserPosition,
		isLoading: isLoadingUserPosition,
		itemsWithDistance: allStoresWithDistance,
		userPosition,
	} = useRelativeDistance({
		items: allStores,
	});

	const allStoreMarkers = allStores?.map((store) => {
		if ('stock' in store) {
			return mapStockStoreMarker({
				store,
				isSelected: selectedStoreId === store.id,
				onSelectClick: () => {
					onStoreSelectClick(store);
				},
			});
		}
		return mapStoreMarker({
			store,
			isSelected: selectedStoreId === store.id,
			onSelectClick: () => {
				onStoreSelectClick(store);
			},
		});
	});

	const nearbyStoreMarkers = (
		is.arrayWithLength(allStoresWithDistance)
			? allStoresWithDistance?.slice(0, 2)
			: nearbyStores
	)?.map((store) => {
		if ('stock' in store) {
			return mapStockStoreMarker({
				store,
				isSelected: selectedStoreId === store.id,
				onSelectClick: () => {
					onStoreSelectClick(store);
				},
			});
		}
		return mapStoreMarker({
			store,
			isSelected: selectedStoreId === store.id,
			onSelectClick: () => {
				onStoreSelectClick(store);
			},
		});
	});

	const filteredStores = filterStores(
		userPosition ? allStoresWithDistance : allStores,
	);

	const isLoading = isLoadingProp || isLoadingUserPosition;
	const searchInputRef = useRef<HTMLInputElement>(null);
	const searchCountRef = useRef<HTMLElement>(null);

	const storeCountText = searchValue
		? tPlural('stores_count_text', filteredStores.length, {
				num: filteredStores.length,
			})
		: userPosition
			? `${t('stores_for_position_header')}: ${tPlural('stores_count_text', allStoresWithDistance.length, { num: allStoresWithDistance.length })}`
			: undefined;

	return (
		<Popover
			isOpen={isOpen}
			id={id}
			titleId={titleId}
			onClose={onClose}
			// Force first page if loading since it has the skeleton.
			currentPageIndex={isLoading ? 0 : storeDetails ? 1 : 0}
			contentClassName={isMapVisible ? 'flex flex-col' : undefined}
			pages={[
				{
					title,
					content: (
						<>
							<ScreenReaderAnnouncementText
								as="div"
								atomic
								text={
									userPositionError ? (
										<Message
											className="mt-4 mb-6"
											variant="error"
											text={
												userPositionError?.PERMISSION_DENIED
													? t('stores_geolocation_permission_error_text')
													: t('stores_geolocation_general_error_text')
											}
										/>
									) : undefined
								}
							/>
							<form
								onSubmit={(event) => {
									event.preventDefault();
									if (storeCountText) {
										searchCountRef.current?.focus();
									} else {
										searchInputRef.current?.blur();
									}
								}}
							>
								<SearchField
									ref={searchInputRef}
									inputLabel={t('store_popover_search_input_label')}
									className="mb-6"
									hasSubmitButton={false}
									hasSearchIcon
									id="store-search"
									value={searchValue}
									onChange={(event) => {
										setSearchValue(event.target.value);
									}}
									onInputClearClick={() => {
										setSearchValue('');
									}}
									placeholder={t('store_popover_search_input_label')}
								/>
							</form>
							<ScreenReaderAnnouncementText
								ref={searchCountRef}
								as="p"
								atomic
								hidden
								// Wait a bit to hopefully let the screen reader finish any
								// search field updates.
								debounceDelay={500}
								tabIndex={-1}
								text={storeCountText}
							/>
							{searchValue && (
								<StoreList
									selectedStoreId={selectedStoreId}
									onStoreSelectClick={onStoreSelectClick}
									isLoading={isLoading}
									stores={filteredStores}
								/>
							)}
							{!searchValue && (
								<>
									<div className="mb-6 flex justify-between">
										<Button
											variant="text"
											onClick={
												userPosition ? clearUserPosition : getUserPosition
											}
											trailingIcon={
												isLoadingUserPosition
													? 'loading'
													: userPosition
														? 'my-location-off'
														: 'my-location'
											}
										>
											{userPosition
												? t('general_forget_position_button')
												: t('general_use_position_button')}
										</Button>

										<Button
											variant="text"
											trailingIcon={isMapVisible ? 'rows' : 'location'}
											onClick={() => setIsMapVisible((current) => !current)}
										>
											{isMapVisible
												? t('stores_show_as_list_button')
												: t('stores_show_on_map_button')}
										</Button>
									</div>

									{!isMapVisible && (
										<ListView
											selectedStoreId={selectedStoreId}
											onStoreSelectClick={onStoreSelectClick}
											isLoading={isLoading}
											userPosition={userPosition}
											storesWithDistance={allStoresWithDistance}
											nearbyStores={nearbyStores}
											allStores={allStores}
										/>
									)}
									{isMapVisible && is.arrayWithLength(allStoreMarkers) && (
										<StoreMap
											stores={allStoreMarkers}
											nearbyStores={nearbyStoreMarkers}
											userPosition={userPosition}
											className="h-auto grow"
											mapClassName="h-full"
										/>
									)}
								</>
							)}
						</>
					),
				},
				{
					title: storeDetails?.name || title,
					content: storeDetails && (
						<>
							<StoreInfo
								facebookLink={storeDetails.facebookLink}
								headingHierarchyStartLevel="h2"
								todaysOpeningHours={storeDetails.todaysOpeningHours}
								storeArea={storeDetails.storeArea}
								streetAddress={storeDetails.streetAddress}
								postalCode={storeDetails.postalCode}
								city={storeDetails.city}
								regularOpeningHours={storeDetails.regularOpeningHours}
								specialOpeningHours={storeDetails.specialOpeningHours}
								addressSectionClassName="flex justify-between"
								socialMediaHeadingClassName="mt-r-xl"
								facebookLinkClassName="mt-2"
								afterAddressContent={
									onBackClick ? (
										<Button
											onClick={onBackClick}
											variant="text"
											className="mt-3 mb-10"
										>
											{t('store_popover_choose_other_store_button')}
										</Button>
									) : undefined
								}
							/>
							<Button
								className="mt-10"
								variant="secondary"
								displayWidth="full"
								target="_blank"
								href={`https://www.google.com/maps/search/?api=1&query=${storeDetails.latitude},${storeDetails.longitude}`}
								data-cy={getTestDataAttrFrom(`${id}-directions`)}
							>
								{t('stores_directions_button')}
							</Button>
							<Button
								className="mt-4"
								displayWidth="full"
								variant="branded"
								href={storeDetails.url}
								data-cy={getTestDataAttrFrom(`${id}-details`)}
							>
								{t('store_popover_visit_store_button')}
							</Button>
						</>
					),
				},
			]}
		/>
	);
}
StorePopover.displayName = 'StorePopover';
